import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { 
  Menu, 
  X, 
  Home, 
  Car, 
  Building2, 
  Settings, 
  Palette,
  Fuel,
  Cog,
  Truck,
  Tag,
  Shield,
  ChevronDown,
  Globe,
  Lock,
  LogOut,
  User,
  ShoppingCart,
  FileText,
  Layers,
  Sparkles,
  BarChart3
} from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

const navigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Vehicles', href: '/vehicles', icon: Car },
  { name: 'Brands', href: '/brands', icon: Building2 },
  { name: 'Models', href: '/models', icon: Settings },
  { name: 'SEO Texts', href: '/seo-texts', icon: FileText },
  { name: 'Statistics', href: '/statistics', icon: BarChart3 },
]

const filterMenus = [
  {
    name: 'Filter Management',
    icon: Settings,
    children: [
      { name: 'Fuel Types', href: '/fuel-types', icon: Fuel },
      { name: 'Colors', href: '/colors', icon: Palette },
      { name: 'Transmissions', href: '/transmissions', icon: Cog },
      { name: 'Body Types', href: '/body-types', icon: Car },
      { name: 'Delivery Statuses', href: '/delivery-statuses', icon: Truck },
      { name: 'Sale Statuses', href: '/sale-statuses', icon: ShoppingCart },
      { name: 'Options', href: '/options', icon: Tag },
      { name: 'Warranty Types', href: '/warranty-types', icon: Shield },
      { name: 'Vehicle Types', href: '/vehicle-types', icon: Layers },
      { name: 'Trims', href: '/trims', icon: Sparkles },
    ]
  }
]

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [openMenus, setOpenMenus] = useState({})
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { currentLanguage, languages, changeLanguage } = useLanguage()
  const { admin, logout } = useAuth()

  const toggleMenu = (menuName) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }))
  }

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  const handleLogout = () => {
    logout()
    toast.success('Logged out successfully')
    navigate('/login')
  }

  // Close user menu when clicking outside
  const userMenuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false)
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex w-64 flex-col bg-white shadow-xl">
          <div className="flex h-16 items-center justify-between px-4">
            <img src="https://carify-llc.fra1.cdn.digitaloceanspaces.com/uploads/logo/logo-red.png" className="max-w-[180px]" alt="Carify Admin" />
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.name}
                </Link>
              )
            })}
            
            {filterMenus.map((menu) => {
              const Icon = menu.icon
              const isOpen = openMenus[menu.name]
              
              return (
                <div key={menu.name}>
                  <button
                    onClick={() => toggleMenu(menu.name)}
                    className="group flex w-full items-center justify-between px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md"
                  >
                    <div className="flex items-center">
                      <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                      {menu.name}
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isOpen && (
                    <div className="ml-6 space-y-1">
                      {menu.children.map((child) => {
                        const ChildIcon = child.icon
                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                              isActive(child.href)
                                ? 'bg-primary-100 text-primary-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <ChildIcon className="mr-3 h-4 w-4 flex-shrink-0" />
                            {child.name}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <Link to={'/'} className="flex h-16 items-center px-4">
            <img src="https://carify-llc.fra1.cdn.digitaloceanspaces.com/uploads/logo/logo-red.png" alt="Carify Admin" />
          </Link>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.name}
                </Link>
              )
            })}
            
            {filterMenus.map((menu) => {
              const Icon = menu.icon
              const isOpen = openMenus[menu.name]
              
              return (
                <div key={menu.name}>
                  <button
                    onClick={() => toggleMenu(menu.name)}
                    className="group flex w-full items-center justify-between px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md"
                  >
                    <div className="flex items-center">
                      <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                      {menu.name}
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isOpen && (
                    <div className="ml-6 space-y-1">
                      {menu.children.map((child) => {
                        const ChildIcon = child.icon
                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                              isActive(child.href)
                                ? 'bg-primary-100 text-primary-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <ChildIcon className="mr-3 h-4 w-4 flex-shrink-0" />
                            {child.name}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>
          
          {/* Language selector and user info */}
          <div className="p-4 border-t border-gray-200 space-y-3">
            <div className="flex items-center space-x-2">
              <Globe className="h-4 w-4 text-gray-500" />
              <select
                value={currentLanguage}
                onChange={(e) => changeLanguage(e.target.value)}
                className="text-sm border-0 bg-transparent text-gray-600 focus:outline-none focus:ring-0"
              >
                {Object.entries(languages).map(([code, lang]) => (
                  <option key={code} value={code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* User info in sidebar */}
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center space-x-2 text-sm">
                <User className="h-4 w-4 text-gray-500" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{admin?.username || 'Admin'}</p>
                  <p className="text-xs text-gray-500 truncate">{admin?.email}</p>
                </div>
              </div>
              <div className="mt-2 space-y-1">
                <Link
                  to="/change-password"
                  className="flex items-center px-2 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded-md"
                  onClick={() => setSidebarOpen(false)}
                >
                  <Lock className="mr-2 h-3 w-3" />
                  Change Password
                </Link>
                <button
                  onClick={() => {
                    setSidebarOpen(false)
                    handleLogout()
                  }}
                  className="flex w-full items-center px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md"
                >
                  <LogOut className="mr-2 h-3 w-3" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />
              
              {/* Language selector for mobile */}
              <div className="lg:hidden">
                <select
                  value={currentLanguage}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="text-sm border-0 bg-transparent text-gray-600 focus:outline-none focus:ring-0"
                >
                  {Object.entries(languages).map(([code, lang]) => (
                    <option key={code} value={code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* User menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-x-2 rounded-lg bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                >
                  <User className="h-5 w-5" />
                  <span className="hidden lg:block">{admin?.username || 'Admin'}</span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200">
                        <p className="font-medium">{admin?.username}</p>
                        <p className="text-xs text-gray-500">{admin?.email}</p>
                      </div>
                      <Link
                        to="/change-password"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Lock className="mr-3 h-4 w-4" />
                        Change Password
                      </Link>
                      <button
                        onClick={() => {
                          setUserMenuOpen(false)
                          handleLogout()
                        }}
                        className="flex w-full items-center px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                      >
                        <LogOut className="mr-3 h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
